/**
 * Attachment / communication query hooks (Patch E26).
 *
 * Calls /api/attachments/*. Mirrors the wire shape inline so the web app
 * doesn't depend on the shared TypeBox runtime.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

// ---------------------------------------------------------------------------
// Types (mirror packages/shared/schemas/attachment.ts)
// ---------------------------------------------------------------------------

export type AttachmentKind = 'DOCUMENT' | 'COMMUNICATION';

export type DocumentType =
  | 'DFA'
  | 'PO'
  | 'SCHEDULE_OF_STORES'
  | 'SUPPLIER_INVOICE'
  | 'CLIENT_INVOICE'
  | 'BG_INSTRUMENT'
  | 'INSPECTION_REPORT'
  | 'LETTER'
  | 'EMAIL_SCAN'
  | 'CHEQUE_COPY'
  | 'CERTIFICATE'
  | 'DRAWING'
  | 'AGREEMENT'
  | 'I_NOTE'
  | 'CRV'
  | 'OTHER';

export type CommType =
  | 'PHONE_CALL'
  | 'MEETING'
  | 'LETTER_RECEIVED'
  | 'LETTER_SENT'
  | 'EMAIL'
  | 'WHATSAPP'
  | 'SMS'
  | 'INTERNAL_NOTE';

export type CommDirection = 'INBOUND' | 'OUTBOUND' | 'INTERNAL';

export interface AttachmentAnchor {
  // Project-tree
  projectId?: string | null;
  projectItemId?: string | null;
  orderBatchId?: string | null;
  bankGuaranteeId?: string | null;
  inspectionEventId?: string | null;
  // Master entity (Patch 2)
  clientId?: string | null;
  supplierId?: string | null;
  masterDepartmentId?: string | null;
}

export interface CommunicationDepartmentRef {
  id: string;
  name: string;
}

export interface Attachment {
  id: string;
  kind: AttachmentKind;
  projectId: string | null;
  projectItemId: string | null;
  orderBatchId: string | null;
  bankGuaranteeId: string | null;
  inspectionEventId: string | null;
  // Patch 2 — master entity anchors + traverse
  clientId: string | null;
  supplierId: string | null;
  masterDepartmentId: string | null;
  traverseClientId: string | null;
  traverseSupplierId: string | null;

  displayName: string | null;
  fileSizeBytes: number | null;
  fileMimeType: string | null;
  documentType: DocumentType | null;
  documentDate: string | null;
  supersedesId: string | null;
  downloadUrl: string | null;

  commType: CommType | null;
  direction: CommDirection | null;
  occurredAt: string | null;
  counterpartyName: string | null;
  counterpartyContactId: string | null;
  referenceNumber: string | null;
  communicationDepartmentId: string | null;
  communicationDepartmentName: string | null;
  copiedDepartments: CommunicationDepartmentRef[];
  subject: string | null;
  notes: string | null;
  // Patch 2 — threading
  threadId: string | null;
  inReplyToId: string | null;

  description: string | null;
  tags: string[];
  createdAt: string;
  createdByUserId: string;
  createdByDisplayName: string;
  updatedAt: string;
  rowVersion: number;
  // Patch 4 — soft-delete timestamp. Threads include deleted comms as
  // placeholders; supersede chains include deleted versions.
  deletedAt: string | null;
}

export interface AttachmentListResponse {
  items: Attachment[];
  total: number;
  page: number;
  pageSize: number;
}

export type CommsSortKey = 'occurredAt' | 'subject' | 'counterpartyName';

export interface AttachmentListFilters {
  projectId?: string;
  kind?: AttachmentKind;
  projectItemId?: string;
  orderBatchId?: string;
  bankGuaranteeId?: string;
  inspectionEventId?: string;
  projectLevelOnly?: boolean;
  // Patch 2 — master entity filters
  clientId?: string;
  supplierId?: string;
  masterDepartmentId?: string;
  /** 'PROJECT' = anything with project_id set; 'MASTER' = direct master anchor. */
  anchorScope?: 'PROJECT' | 'MASTER';
  documentType?: DocumentType;
  commType?: CommType;
  direction?: CommDirection;
  tag?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  hasAttachment?: boolean;
  page?: number;
  pageSize?: number;
  // Patch 5 — sortable columns
  sortBy?: CommsSortKey;
  sortDir?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const attachmentsKey = (filters: AttachmentListFilters) =>
  ['attachments', 'list', filters] as const;
export const attachmentKey = (id: string) => ['attachments', 'one', id] as const;
export const attachmentTagsKey = (q: string) => ['attachments', 'tags', q] as const;

// ---------------------------------------------------------------------------
// Hooks — read
// ---------------------------------------------------------------------------

function buildQs(filters: AttachmentListFilters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useAttachmentsList(filters: AttachmentListFilters) {
  return useQuery({
    queryKey: attachmentsKey(filters),
    queryFn: ({ signal }) =>
      apiRequest<AttachmentListResponse>(`/api/attachments${buildQs(filters)}`, { signal }),
  });
}

export function useAttachment(id: string | undefined) {
  return useQuery({
    queryKey: id ? attachmentKey(id) : ['attachments', 'one', 'disabled'],
    queryFn: ({ signal }) => apiRequest<Attachment>(`/api/attachments/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export interface TagAutocompleteEntry {
  name: string;
  usageCount: number;
}
export interface TagAutocompleteResponse {
  tags: TagAutocompleteEntry[];
}

export function useTagAutocomplete(q: string) {
  const query = q.trim().toLowerCase();
  return useQuery({
    queryKey: attachmentTagsKey(query),
    queryFn: ({ signal }) =>
      apiRequest<TagAutocompleteResponse>(
        `/api/attachments/tags${query ? `?q=${encodeURIComponent(query)}` : ''}`,
        { signal },
      ),
    staleTime: 30_000, // tags don't change often
  });
}

// ---------------------------------------------------------------------------
// Hooks — mutations
// ---------------------------------------------------------------------------

export interface UploadUrlRequest {
  filename: string;
  mimeType: string;
  sizeBytes: number;
}
export interface UploadUrlResponse {
  uploadUrl: string;
  storageKey: string;
  expiresIn: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Drives the two-step upload flow for a single document:
 *   1. POST /api/attachments/upload-url with {filename, mimeType, sizeBytes}
 *   2. PUT the file bytes to the returned URL (streams direct to MinIO)
 *
 * Returns the storage key for step 3 (commit via documents or communications
 * endpoint). On error throws.
 */
export async function uploadFileToMinio(
  file: File,
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<{ storageKey: string }> {
  const presign = await apiRequest<UploadUrlResponse>('/api/attachments/upload-url', {
    method: 'POST',
    body: { filename: file.name, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size },
    signal,
  });

  // Fetch can't report progress. Use XMLHttpRequest for the PUT so we can
  // surface a progress bar to the user.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      onProgress({
        loaded: e.loaded,
        total: e.total,
        percent: Math.round((e.loaded / e.total) * 100),
      });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(file);
  });

  return { storageKey: presign.storageKey };
}

export interface DocumentCreateInput {
  storageKey: string;
  fileSizeBytes: number;
  fileMimeType: string;
  displayName: string;
  documentType: DocumentType;
  documentDate: string;
  description?: string | null;
  supersedesId?: string | null;
  anchor: AttachmentAnchor;
  tags?: string[];
}

export function useCreateDocument() {
  const queryClient = useQueryClient();
  return useMutation<Attachment, ApiError, DocumentCreateInput>({
    mutationFn: (body) =>
      apiRequest<Attachment>('/api/attachments/documents', { method: 'POST', body }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['attachments'] });
      queryClient.setQueryData(attachmentKey(created.id), created);
    },
  });
}

export interface CommunicationAttachedDoc {
  storageKey: string;
  fileSizeBytes: number;
  fileMimeType: string;
  displayName: string;
  documentType: DocumentType;
  documentDate: string;
}

export interface CommunicationCreateInput {
  commType: CommType;
  direction: CommDirection;
  occurredAt: string;
  counterpartyName?: string | null;
  counterpartyContactId?: string | null;
  referenceNumber?: string | null;
  communicationDepartmentId?: string | null;
  copiedDepartmentIds?: string[];
  subject: string;
  notes?: string | null;
  anchor: AttachmentAnchor;
  tags?: string[];
  attachedDocs?: CommunicationAttachedDoc[];
  /** Patch 2 — set to reply to an existing comm. Null/undefined starts a new thread. */
  inReplyToId?: string | null;
}

export function useLogCommunication() {
  const queryClient = useQueryClient();
  return useMutation<Attachment, ApiError, CommunicationCreateInput>({
    mutationFn: (body) =>
      apiRequest<Attachment>('/api/attachments/communications', { method: 'POST', body }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['attachments'] });
      queryClient.setQueryData(attachmentKey(created.id), created);
    },
  });
}

export interface CommunicationUpdateInput {
  subject?: string;
  notes?: string | null;
  description?: string | null;
  occurredAt?: string;
  counterpartyName?: string | null;
  referenceNumber?: string | null;
  communicationDepartmentId?: string | null;
  copiedDepartmentIds?: string[];
  tags?: string[];
  rowVersion: number;
}

export function useUpdateCommunication(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Attachment, ApiError, CommunicationUpdateInput>({
    mutationFn: (body) =>
      apiRequest<Attachment>(`/api/attachments/${id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['attachments'] });
      queryClient.setQueryData(attachmentKey(updated.id), updated);
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiRequest<void>(`/api/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Per-entity counts (drives inline `📎 N` badges)
//
// Single payload per project. Components on the project detail page read
// their bucket from the same cached response — no per-entity request fan-out.
// ---------------------------------------------------------------------------

export interface AttachmentCountsResponse {
  projectLevel: number;
  byPart: Record<string, number>;
  byBatch: Record<string, number>;
  byBg: Record<string, number>;
  byInspection: Record<string, number>;
}

export const attachmentCountsKey = (projectId: string) =>
  ['attachments', 'counts', projectId] as const;

export function useAttachmentCounts(projectId: string, enabled = true) {
  return useQuery({
    queryKey: attachmentCountsKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<AttachmentCountsResponse>(
        `/api/attachments/counts?projectId=${encodeURIComponent(projectId)}`,
        { signal },
      ),
    enabled: enabled && !!projectId,
    // The badge data is heavily read but rarely changes within a session.
    // staleTime of 60s keeps it warm without making it feel stale.
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Patch 2 — Thread expansion
// ---------------------------------------------------------------------------

export interface ThreadMessage {
  message: Attachment;
  attachedDocs: Attachment[];
}
export interface ThreadResponse {
  threadId: string;
  messages: ThreadMessage[];
}

export const threadKey = (threadId: string) => ['attachments', 'thread', threadId] as const;

export function threadAttachmentsPdfUrl(threadId: string): string {
  return `/api/attachments/threads/${encodeURIComponent(threadId)}/attachments.pdf`;
}

export function useThread(threadId: string | undefined) {
  return useQuery({
    queryKey: threadId ? threadKey(threadId) : ['attachments', 'thread', 'disabled'],
    queryFn: ({ signal }) =>
      apiRequest<ThreadResponse>(`/api/attachments/threads/${threadId}`, { signal }),
    enabled: Boolean(threadId),
  });
}

// ---------------------------------------------------------------------------
// Patch 2 — Last contact (Receivables + Forecast "last contact" indicators)
// ---------------------------------------------------------------------------

export interface LastContactSummary {
  attachmentId: string;
  threadId: string;
  subject: string;
  occurredAt: string;
  direction: CommDirection;
  commType: CommType;
  counterpartyName: string | null;
}

export interface LastContactResponse {
  byProject: Record<string, LastContactSummary>;
  byBg: Record<string, LastContactSummary>;
}

export const lastContactKey = (projectIds: string[], bgIds: string[]) =>
  ['attachments', 'last-contact', projectIds.slice().sort().join(','), bgIds.slice().sort().join(',')] as const;

export function useLastContact(projectIds: string[], bgIds: string[]) {
  const enabled = projectIds.length > 0 || bgIds.length > 0;
  return useQuery({
    queryKey: lastContactKey(projectIds, bgIds),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (projectIds.length) params.set('projectIds', projectIds.join(','));
      if (bgIds.length) params.set('bankGuaranteeIds', bgIds.join(','));
      return apiRequest<LastContactResponse>(
        `/api/attachments/last-contact?${params.toString()}`,
        { signal },
      );
    },
    enabled,
    // "Last contact" doesn't change often; 60s stale is plenty.
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Patch 2 — Master entity attachments (Direct + Related from projects)
// ---------------------------------------------------------------------------

export type MasterEntityKind = 'CLIENT' | 'SUPPLIER' | 'DEPARTMENT';

export interface MasterEntityAttachmentsResponse {
  direct: Attachment[];
  related: Attachment[];
}

export const masterAttachmentsKey = (kind: MasterEntityKind, id: string) =>
  ['attachments', 'master', kind, id] as const;

export function useMasterEntityAttachments(
  kind: MasterEntityKind | undefined,
  id: string | undefined,
) {
  return useQuery({
    queryKey: kind && id ? masterAttachmentsKey(kind, id) : ['attachments', 'master', 'disabled'],
    queryFn: ({ signal }) =>
      apiRequest<MasterEntityAttachmentsResponse>(
        `/api/attachments/master?kind=${kind}&id=${id}`,
        { signal },
      ),
    enabled: Boolean(kind && id),
  });
}

// ---------------------------------------------------------------------------
// Patch 3 — Supersede chain
// ---------------------------------------------------------------------------

export interface SupersedeChainVersion {
  attachment: Attachment;
  isAnchor: boolean;
  isDeleted: boolean;
}

export interface SupersedeChainResponse {
  anchorId: string;
  versions: SupersedeChainVersion[];
  truncated: boolean;
}

export const supersedeChainKey = (id: string) =>
  ['attachments', 'supersede-chain', id] as const;

export function useSupersedeChain(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: id ? supersedeChainKey(id) : ['attachments', 'supersede-chain', 'disabled'],
    queryFn: ({ signal }) =>
      apiRequest<SupersedeChainResponse>(
        `/api/attachments/${id}/supersede-chain`,
        { signal },
      ),
    enabled: enabled && Boolean(id),
    // Chains change only when a new version is uploaded; 30s stale is fine.
    staleTime: 30_000,
  });
}
